import React, { useState } from 'react';
import { getDailyForecastSections } from '../../utils/weatherModalUtils';

// Codes météo simplifiés
const WEATHER_CODES = {
  0: { icon: '☀️', description: { fr: 'Ciel dégagé', en: 'Clear sky' } },
  1: { icon: '🌤️', description: { fr: 'Principalement dégagé', en: 'Mainly clear' } },
  2: { icon: '⛅', description: { fr: 'Partiellement nuageux', en: 'Partly cloudy' } },
  3: { icon: '☁️', description: { fr: 'Couvert', en: 'Overcast' } },
  45: { icon: '🌫️', description: { fr: 'Brouillard', en: 'Fog' } },
  48: { icon: '🌫️', description: { fr: 'Brouillard givrant', en: 'Depositing rime fog' } },
  51: { icon: '🌦️', description: { fr: 'Bruine légère', en: 'Light drizzle' } },
  53: { icon: '🌦️', description: { fr: 'Bruine modérée', en: 'Moderate drizzle' } },
  55: { icon: '🌧️', description: { fr: 'Bruine dense', en: 'Dense drizzle' } },
  56: { icon: '🌦️', description: { fr: 'Bruine verglaçante légère', en: 'Light freezing drizzle' } },
  57: { icon: '🌧️', description: { fr: 'Bruine verglaçante dense', en: 'Dense freezing drizzle' } },
  61: { icon: '🌦️', description: { fr: 'Pluie légère', en: 'Slight rain' } },
  63: { icon: '🌧️', description: { fr: 'Pluie modérée', en: 'Moderate rain' } },
  65: { icon: '🌧️', description: { fr: 'Pluie forte', en: 'Heavy rain' } },
  66: { icon: '🌦️', description: { fr: 'Pluie verglaçante légère', en: 'Light freezing rain' } },
  67: { icon: '🌧️', description: { fr: 'Pluie verglaçante forte', en: 'Heavy freezing rain' } },
  71: { icon: '🌨️', description: { fr: 'Chute de neige légère', en: 'Slight snow fall' } },
  73: { icon: '🌨️', description: { fr: 'Chute de neige modérée', en: 'Moderate snow fall' } },
  75: { icon: '❄️', description: { fr: 'Chute de neige forte', en: 'Heavy snow fall' } },
  77: { icon: '🌨️', description: { fr: 'Grains de neige', en: 'Snow grains' } },
  80: { icon: '🌦️', description: { fr: 'Averses légères', en: 'Slight rain showers' } },
  81: { icon: '🌧️', description: { fr: 'Averses modérées', en: 'Moderate rain showers' } },
  82: { icon: '🌧️', description: { fr: 'Averses violentes', en: 'Violent rain showers' } },
  85: { icon: '🌨️', description: { fr: 'Averses de neige légères', en: 'Slight snow showers' } },
  86: { icon: '❄️', description: { fr: 'Averses de neige fortes', en: 'Heavy snow showers' } },
  95: { icon: '⛈️', description: { fr: 'Orage', en: 'Thunderstorm' } },
  96: { icon: '⛈️', description: { fr: 'Orage avec grêle légère', en: 'Thunderstorm with slight hail' } },
  99: { icon: '⛈️', description: { fr: 'Orage avec grêle forte', en: 'Thunderstorm with heavy hail' } }
};

const confidenceLabels = {
  fr: { high: 'Précision élevée', medium: 'Prévision', trend: 'Tendance' },
  en: { high: 'High confidence', medium: 'Forecast', trend: 'Trend' }
};

const DailyForecastExpandable = ({ weatherData, currentLang }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (!weatherData?.daily?.time) return null;

  const { forecast, trend } = getDailyForecastSections(weatherData);
  const labels = confidenceLabels[currentLang] || confidenceLabels.fr;
  const forecastTitle = currentLang === 'fr' ? 'PRÉVISIONS 7 JOURS' : '7-DAY FORECAST';
  const trendTitle = currentLang === 'fr' ? 'TENDANCE 8-16 JOURS' : '8-16 DAY TREND';

  const renderDays = (dailyData, variant = 'forecast', startOffset = 0) => (
    <div className={`daily-list-ios daily-list-ios-${variant}`}>
      {dailyData.time.map((date, index) => {
        const dayDate = new Date(date);
        const dayIndex = startOffset + index;
        const isToday = dayIndex === 0;
        const dayName = isToday
          ? (currentLang === 'fr' ? 'Auj.' : 'Today')
          : dayDate.toLocaleDateString(currentLang === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short' });

        const tempMax = Math.round(dailyData?.temperature_2m_max?.[index] || 0);
        const tempMin = Math.round(dailyData?.temperature_2m_min?.[index] || 0);
        const code = dailyData?.weather_code?.[index] || 0;
        const rainProb = dailyData?.precipitation_probability_max?.[index] || 0;
        const confidence = dailyData.confidenceByDay?.[index]?.confidence;

        // Calcul de la couleur de la barre de température
        const tempRange = 40;
        const tempPercentage = Math.max(0, Math.min(100, ((tempMax - (-10)) / tempRange) * 100));
        const barColor = tempMax > 25 ? '#ff9500' : tempMax > 15 ? '#ffcc02' : '#34c759';

        return (
          <div key={date} className={`daily-item-ios ${variant === 'trend' ? 'daily-item-ios-trend' : ''}`}>
            <div className="daily-day-ios">
              <div className="day-name-ios">{dayName}</div>
              <div className="day-date-ios">{dayDate.getDate()}</div>
              {confidence && (
                <span className={`daily-confidence-ios confidence-${confidence}`}>
                  {labels[confidence] || confidence}
                </span>
              )}
            </div>

            <div className="daily-icon-ios">
              {WEATHER_CODES[code]?.icon || '🌤️'}
            </div>

            {rainProb > 0 && (
              <div className="daily-rain-ios">
                {rainProb}%
              </div>
            )}

            <div className="daily-temp-bar-ios">
              <div className="temp-bar-container">
                <div
                  className="temp-bar"
                  style={{
                    background: `linear-gradient(90deg, ${barColor} 0%, ${barColor}80 100%)`,
                    width: `${Math.max(20, tempPercentage)}%`
                  }}
                ></div>
              </div>
            </div>

            <div className="daily-temps-ios">
              <span className="temp-min-ios">{tempMin}°</span>
              <span className="temp-max-ios">{tempMax}°</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="daily-forecast-ios">
      <div
        className="daily-forecast-header"
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(); } }}
      >
        <div className="forecast-header">
          <span className="forecast-icon">📅</span>
          <span className="forecast-title">{forecastTitle}</span>
        </div>
        <button className={`daily-forecast-toggle ${isExpanded ? 'expanded' : ''}`}>
          ▼
        </button>
      </div>

      <div className={`daily-forecast-content ${isExpanded ? 'expanded' : ''}`}>
        {renderDays(forecast)}
        {trend?.time?.length > 0 && (
          <div className="daily-trend-ios">
            <div className="daily-trend-title-ios">{trendTitle}</div>
            {renderDays(trend, 'trend', 7)}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyForecastExpandable;
